import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, Alert, Avatar, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, TextField, Chip, Tabs, Tab, CircularProgress,
  Tooltip, InputAdornment, Popover, Checkbox,
} from '@mui/material';
import {
  X, QrCode, Trash2, Link, Unlink, Ban, RefreshCw, Plus, History,
  Search, Package, Eye, ClipboardCopy, Download, Maximize2, CheckSquare,
  AlertTriangle,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import QRCode from 'qrcode';
import JSZip from 'jszip';
import { format } from 'date-fns';
import api from '../config/api';
import {
  StyledTableContainer, TableSkeleton, FilterBar, SearchInput,
  StyledPagination, EmptyState, StatusChip, FilterSelect,
} from '../components/TableShared';

const ff = "'Poppins', sans-serif";

const formatDate = (d) => {
  if (!d) return 'N/A';
  try { return format(new Date(d), 'MMM dd, yyyy'); } catch { return 'N/A'; }
};
const formatDateTime = (d) => {
  if (!d) return 'N/A';
  try { return format(new Date(d), 'MMM dd, yyyy hh:mm a'); } catch { return 'N/A'; }
};

const STATUS_CONFIG = {
  CREATED: { color: '#3182CE', label: 'Created' },
  CONNECTED: { color: '#38A169', label: 'Connected' },
  DISCONNECTED: { color: '#DD6B20', label: 'Disconnected' },
  DEACTIVATED: { color: '#E53E3E', label: 'Deactivated' },
};

const ACTION_CONFIG = {
  CREATED: { color: '#3182CE', label: 'Created' },
  CONNECTED: { color: '#38A169', label: 'Connected' },
  DISCONNECTED: { color: '#DD6B20', label: 'Disconnected' },
  DEACTIVATED: { color: '#E53E3E', label: 'Deactivated' },
  REACTIVATED: { color: '#805AD5', label: 'Reactivated' },
};

// ─── Public URL encoded in each QR ──────────────────────────
// Scanning the QR with any camera/app opens this URL. The visible
// text label below the QR still shows the short code (e.g. OKTREAT-XXXXXX)
// so it stays human-readable on the printed tag.
const PUBLIC_WEB_URL = (process.env.REACT_APP_PUBLIC_WEB_URL || 'https://ok-treat-admin-dashboard-bufbf.ondigitalocean.app').replace(/\/+$/, '');
const buildQrUrl = (code) => `${PUBLIC_WEB_URL}/tag/${code}`;

// Color of the QR modules themselves — used by both on-screen previews and exported PNG
const QR_COLOR = '#3BA6B6';

// ─── Export PNG (QR + code text only, transparent background) ─
// Pure asset for placing on a physical tag layout where brand
// text is printed separately. Tweak sizes here.
const EXPORT_BRAND = {
  canvasW: 500,
  canvasH: 500,
  qrSize: 400,
  qrTop: 20,
  codeColor: '#FFFFFF',
  qrColor: QR_COLOR,
  codeFont: 'bold 34px monospace',
};

async function drawQrPngBlob(code) {
  const { canvasW, canvasH, qrSize, qrTop, codeColor, qrColor, codeFont } = EXPORT_BRAND;

  const canvas = document.createElement('canvas');
  canvas.width = canvasW;
  canvas.height = canvasH;
  const ctx = canvas.getContext('2d');
  // Background intentionally left untouched (transparent).

  const qrCanvas = document.createElement('canvas');
  // `light: '#00000000'` (RGBA with 0 alpha) keeps the QR background transparent.
  await QRCode.toCanvas(qrCanvas, buildQrUrl(code), {
    width: qrSize,
    margin: 1,
    errorCorrectionLevel: 'H',
    color: { dark: qrColor, light: '#00000000' },
  });
  ctx.drawImage(qrCanvas, (canvasW - qrSize) / 2, qrTop, qrSize, qrSize);

  ctx.fillStyle = codeColor;
  ctx.font = codeFont;
  ctx.textAlign = 'center';
  ctx.fillText(code, canvasW / 2, qrTop + qrSize + 50);

  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
}

// ─── Reusable danger-action confirm dialog ──────────────────
function ConfirmDialog({ open, title, body, confirmLabel = 'Delete', onCancel, onConfirm, busy }) {
  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontFamily: ff, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
        <AlertTriangle size={20} color="#E53E3E" />
        {title}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ fontFamily: ff, fontSize: 13, color: '#2D3748', lineHeight: 1.6 }}>{body}</Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={busy} sx={{ fontFamily: ff, textTransform: 'none', color: '#718096' }}>Cancel</Button>
        <Button
          onClick={onConfirm}
          disabled={busy}
          variant="contained"
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <Trash2 size={14} />}
          sx={{ fontFamily: ff, textTransform: 'none', background: '#E53E3E', borderRadius: '8px', '&:hover': { background: '#C53030' } }}
        >
          {busy ? 'Deleting...' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Generate QR Dialog ─────────────────────────────────────
function GenerateDialog({ open, onClose, onGenerated }) {
  const [count, setCount] = useState('1');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    const qty = parseInt(count) || 1;
    if (qty < 1 || qty > 100) {
      setError('Enter a number between 1 and 100');
      return;
    }
    try {
      setLoading(true);
      setError('');
      await api.post('/admin/qrcodes', { count: qty, notes });
      onGenerated(qty);
      setCount('1');
      setNotes('');
      onClose();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to generate');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontFamily: ff, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Plus size={18} color="#32A6D8" /> Generate QR Codes
        </Box>
        <IconButton size="small" onClick={onClose}><X size={18} /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && <Alert severity="error" sx={{ fontSize: 12 }}>{error}</Alert>}
        <TextField
          label="Quantity (1-100)"
          type="number"
          size="small"
          value={count}
          onChange={(e) => setCount(e.target.value)}
          inputProps={{ min: 1, max: 100 }}
          sx={{ '& .MuiOutlinedInput-root': { fontFamily: ff, fontSize: 13, borderRadius: '10px' } }}
        />
        <TextField
          label="Notes (optional)"
          size="small"
          multiline
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g., Batch for printing, event giveaway..."
          sx={{ '& .MuiOutlinedInput-root': { fontFamily: ff, fontSize: 13, borderRadius: '10px' } }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ fontFamily: ff, textTransform: 'none', color: '#718096' }}>Cancel</Button>
        <Button
          onClick={handleGenerate}
          disabled={loading}
          variant="contained"
          sx={{ fontFamily: ff, textTransform: 'none', background: '#32A6D8', borderRadius: '8px', '&:hover': { background: '#2B8FB8' } }}
        >
          {loading ? <CircularProgress size={18} color="inherit" /> : `Generate ${parseInt(count) || 1} Code${(parseInt(count) || 1) !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Connect Pet Dialog ─────────────────────────────────────
function ConnectDialog({ open, qrCode, onClose, onConnected }) {
  const [search, setSearch] = useState('');
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const handleSearch = async () => {
    if (!search.trim()) return;
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/admin/pets', { params: { search, limit: 20 } });
      setPets(res.data?.data?.pets || []);
    } catch (e) {
      setError('Failed to search pets');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (petId) => {
    try {
      setConnecting(petId);
      setError('');
      await api.put(`/admin/qrcodes/${qrCode.id}/connect`, { petId, reason });
      onConnected();
      setSearch('');
      setPets([]);
      setReason('');
      onClose();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to connect');
    } finally {
      setConnecting(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: ff, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Link size={18} color="#38A169" /> Connect {qrCode?.code} to Pet
        </Box>
        <IconButton size="small" onClick={onClose}><X size={18} /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && <Alert severity="error" sx={{ fontSize: 12 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search pet by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ '& .MuiOutlinedInput-root': { fontFamily: ff, fontSize: 13, borderRadius: '10px' } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search size={16} color="#A0AEC0" /></InputAdornment>,
            }}
          />
          <Button onClick={handleSearch} variant="contained" size="small" sx={{ fontFamily: ff, textTransform: 'none', background: '#32A6D8', borderRadius: '10px', minWidth: 80, '&:hover': { background: '#2B8FB8' } }}>
            Search
          </Button>
        </Box>
        <TextField
          size="small"
          fullWidth
          placeholder="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{ '& .MuiOutlinedInput-root': { fontFamily: ff, fontSize: 13, borderRadius: '10px' } }}
        />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : (
          <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
            {pets.map((pet) => (
              <Box
                key={pet.id}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, px: 1,
                  borderBottom: '1px solid #F7FAFC', cursor: 'pointer',
                  '&:hover': { background: '#F7FAFC' }, borderRadius: '8px',
                }}
                onClick={() => handleConnect(pet.id)}
              >
                <Avatar src={pet.photoUrl} sx={{ width: 36, height: 36, fontSize: 13 }}>{pet.name?.[0]}</Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ fontFamily: ff, fontSize: 13, fontWeight: 500, color: '#1A202C' }}>{pet.name}</Box>
                  <Box sx={{ fontFamily: ff, fontSize: 11, color: '#A0AEC0' }}>{pet.type}{pet.breed ? ` - ${pet.breed}` : ''}</Box>
                </Box>
                {connecting === pet.id ? (
                  <CircularProgress size={18} />
                ) : (
                  <Chip label="Connect" size="small" sx={{ fontFamily: ff, fontSize: 10, fontWeight: 600, background: '#C6F6D5', color: '#22543D' }} />
                )}
              </Box>
            ))}
            {pets.length === 0 && search.trim() && !loading && (
              <Box sx={{ fontFamily: ff, fontSize: 13, color: '#A0AEC0', textAlign: 'center', py: 4 }}>No pets found</Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── QR Detail / History Dialog ─────────────────────────────
function DetailDialog({ open, qrId, onClose, onUpdated }) {
  const [data, setData] = useState(null);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [reason, setReason] = useState('');
  const [tab, setTab] = useState(0);

  const fetchDetail = useCallback(async () => {
    if (!qrId) return;
    try {
      setLoading(true);
      const res = await api.get(`/admin/qrcodes/${qrId}`);
      setData(res.data.data.qrCode);
      setScans(res.data.data.scans || []);
    } catch (e) {
      console.error('Error fetching QR detail:', e);
    } finally {
      setLoading(false);
    }
  }, [qrId]);

  useEffect(() => {
    if (open) {
      setData(null);
      setScans([]);
      setTab(0);
      fetchDetail();
    }
  }, [open, fetchDetail]);

  const handleAction = async (action) => {
    try {
      setActionLoading(action);
      await api.put(`/admin/qrcodes/${qrId}/${action}`, { reason });
      setReason('');
      fetchDetail();
      onUpdated();
    } catch (e) {
      alert(e.response?.data?.message || `Failed to ${action}`);
    } finally {
      setActionLoading('');
    }
  };

  const status = data?.status;
  const statusConf = STATUS_CONFIG[status] || STATUS_CONFIG.CREATED;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontFamily: ff, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <QrCode size={20} color="#32A6D8" />
          {data?.code || 'QR Code Detail'}
        </Box>
        <IconButton size="small" onClick={onClose}><X size={18} /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : data && (
          <Box>
            {/* Top Info Bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, p: 3, background: '#F7FAFC', flexWrap: 'wrap' }}>
              <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Box sx={{ p: 1.5, background: '#FFF', borderRadius: '12px', border: '1px solid #EDF2F7' }}>
                  <QRCodeSVG value={buildQrUrl(data.code)} size={120} level="H" fgColor={QR_COLOR} />
                </Box>
                <Box sx={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 700, color: '#1A202C', letterSpacing: 1 }}>{data.code}</Box>
                <StatusChip label={statusConf.label} color={statusConf.color} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                {data.pet ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar src={data.pet.photoUrl} sx={{ width: 40, height: 40 }}>{data.pet.name?.[0]}</Avatar>
                    <Box>
                      <Box sx={{ fontFamily: ff, fontSize: 13, fontWeight: 500 }}>{data.pet.name}</Box>
                      <Box sx={{ fontFamily: ff, fontSize: 11, color: '#718096' }}>{data.pet.type}{data.pet.breed ? ` - ${data.pet.breed}` : ''}</Box>
                      {data.pet.user && <Box sx={{ fontFamily: ff, fontSize: 11, color: '#32A6D8' }}>Owner: {data.pet.user.fullName}</Box>}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ fontFamily: ff, fontSize: 13, color: '#A0AEC0' }}>No pet linked</Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, fontSize: 11, fontFamily: ff, color: '#718096' }}>
                <Box>Created: {formatDateTime(data.createdAt)}</Box>
                <Box>By: {data.createdBy?.fullName || 'Admin'}</Box>
                {data.connectedAt && <Box>Connected: {formatDateTime(data.connectedAt)}</Box>}
              </Box>
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2, borderBottom: '1px solid #EDF2F7', flexWrap: 'wrap' }}>
              <TextField
                size="small"
                placeholder="Reason (optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                sx={{ flex: 1, minWidth: 200, '& .MuiOutlinedInput-root': { fontFamily: ff, fontSize: 12, borderRadius: '8px' } }}
              />
              {(status === 'CREATED' || status === 'DISCONNECTED') && (
                <Button size="small" variant="outlined" startIcon={<Link size={14} />} disabled sx={{ fontFamily: ff, textTransform: 'none', fontSize: 12, borderRadius: '8px' }}>
                  Connect (use table action)
                </Button>
              )}
              {status === 'CONNECTED' && (
                <Button
                  size="small" variant="outlined" color="warning"
                  startIcon={actionLoading === 'disconnect' ? <CircularProgress size={14} /> : <Unlink size={14} />}
                  onClick={() => handleAction('disconnect')}
                  disabled={!!actionLoading}
                  sx={{ fontFamily: ff, textTransform: 'none', fontSize: 12, borderRadius: '8px' }}
                >
                  Disconnect
                </Button>
              )}
              {status !== 'DEACTIVATED' && (
                <Button
                  size="small" variant="outlined" color="error"
                  startIcon={actionLoading === 'deactivate' ? <CircularProgress size={14} /> : <Ban size={14} />}
                  onClick={() => handleAction('deactivate')}
                  disabled={!!actionLoading}
                  sx={{ fontFamily: ff, textTransform: 'none', fontSize: 12, borderRadius: '8px' }}
                >
                  Deactivate
                </Button>
              )}
              {status === 'DEACTIVATED' && (
                <Button
                  size="small" variant="outlined" color="secondary"
                  startIcon={actionLoading === 'reactivate' ? <CircularProgress size={14} /> : <RefreshCw size={14} />}
                  onClick={() => handleAction('reactivate')}
                  disabled={!!actionLoading}
                  sx={{ fontFamily: ff, textTransform: 'none', fontSize: 12, borderRadius: '8px' }}
                >
                  Reactivate
                </Button>
              )}
            </Box>

            {/* Tabs */}
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 3, '& .MuiTab-root': { fontFamily: ff, fontSize: 12, textTransform: 'none' } }}>
              <Tab label={`History (${data.history?.length || 0})`} />
              <Tab label={`Scans (${scans.length})`} />
            </Tabs>

            {/* History Tab */}
            {tab === 0 && (
              <Box sx={{ px: 3, py: 2, maxHeight: 350, overflow: 'auto' }}>
                {data.history?.length > 0 ? data.history.map((entry, i) => {
                  const conf = ACTION_CONFIG[entry.action] || ACTION_CONFIG.CREATED;
                  return (
                    <Box key={entry.id} sx={{ display: 'flex', gap: 2, mb: 0 }}>
                      {/* Timeline dot + line */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 24 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: conf.color, mt: 0.5, flexShrink: 0 }} />
                        {i < data.history.length - 1 && <Box sx={{ width: 2, flex: 1, background: '#EDF2F7' }} />}
                      </Box>
                      {/* Content */}
                      <Box sx={{ pb: 2.5, flex: 1 }}>
                        <Box sx={{ fontFamily: ff, fontSize: 13, fontWeight: 600, color: conf.color }}>{conf.label}</Box>
                        {entry.petName && <Box sx={{ fontFamily: ff, fontSize: 12, color: '#2D3748' }}>Pet: {entry.petName}</Box>}
                        {entry.reason && <Box sx={{ fontFamily: ff, fontSize: 12, color: '#718096', fontStyle: 'italic' }}>"{entry.reason}"</Box>}
                        <Box sx={{ fontFamily: ff, fontSize: 11, color: '#A0AEC0' }}>
                          {entry.performedBy?.fullName || 'Admin'} &middot; {formatDateTime(entry.createdAt)}
                        </Box>
                      </Box>
                    </Box>
                  );
                }) : (
                  <Box sx={{ fontFamily: ff, fontSize: 13, color: '#A0AEC0', textAlign: 'center', py: 4 }}>No history yet</Box>
                )}
              </Box>
            )}

            {/* Scans Tab */}
            {tab === 1 && (
              <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
                {scans.length > 0 ? (
                  <StyledTableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Scanned By</TableCell>
                          <TableCell>Location</TableCell>
                          <TableCell>Scanned At</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {scans.map((scan) => (
                          <TableRow key={scan.id}>
                            <TableCell>{scan.scannedByUser?.fullName || 'Anonymous'}</TableCell>
                            <TableCell>{scan.city || scan.address || 'N/A'}</TableCell>
                            <TableCell>{formatDateTime(scan.scannedAt)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </StyledTableContainer>
                ) : (
                  <Box sx={{ fontFamily: ff, fontSize: 13, color: '#A0AEC0', textAlign: 'center', py: 4 }}>No scans recorded</Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ fontFamily: ff, textTransform: 'none' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main QR Codes Page ─────────────────────────────────────
export default function QRCodes() {
  const [qrCodes, setQrCodes] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const debounceTimer = useRef(null);

  // Dialogs
  const [generateOpen, setGenerateOpen] = useState(false);
  const [connectQR, setConnectQR] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [qrPreview, setQrPreview] = useState(null); // { anchorEl, code }

  // Selection / export mode
  const [selectionMode, setSelectionMode] = useState(false);
  const [selected, setSelected] = useState(new Map()); // id -> code
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ current: 0, total: 0 });
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmState, setConfirmState] = useState(null); // { title, body, confirmLabel, onConfirm, busy }
  const [deletingUnlinked, setDeletingUnlinked] = useState(false);
  const [actionResult, setActionResult] = useState(null); // { severity, text } banner above stats

  // Clear selection whenever filters change
  useEffect(() => {
    setSelected(new Map());
  }, [search, statusFilter]);

  const toggleRow = (qr) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(qr.id)) next.delete(qr.id);
      else next.set(qr.id, qr.code);
      return next;
    });
  };

  const togglePage = () => {
    setSelected((prev) => {
      const next = new Map(prev);
      const allOnPageSelected = qrCodes.length > 0 && qrCodes.every((qr) => next.has(qr.id));
      if (allOnPageSelected) qrCodes.forEach((qr) => next.delete(qr.id));
      else qrCodes.forEach((qr) => next.set(qr.id, qr.code));
      return next;
    });
  };

  const enterSelectMode = () => { setSelectionMode(true); };
  const cancelSelectMode = () => { setSelectionMode(false); setSelected(new Map()); };

  const handleExportSelected = async () => {
    if (selected.size === 0) return;
    try {
      setExporting(true);
      setExportProgress({ current: 0, total: selected.size });

      // ensure Poppins is available for canvas text rendering
      if (document.fonts?.ready) { try { await document.fonts.ready; } catch { /* ignore */ } }

      const zip = new JSZip();
      const entries = Array.from(selected.entries()); // [[id, code], ...]
      let i = 0;
      for (const [, code] of entries) {
        const blob = await drawQrPngBlob(code);
        if (blob) zip.file(`${code}.png`, blob);
        i += 1;
        setExportProgress({ current: i, total: entries.length });
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      const stamp = format(new Date(), 'yyyyMMdd-HHmmss');
      a.href = url;
      a.download = `oktreat-qrcodes-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      cancelSelectMode();
    } catch (e) {
      console.error('Export failed:', e);
      alert('Failed to export QR codes.');
    } finally {
      setExporting(false);
      setExportProgress({ current: 0, total: 0 });
    }
  };

  const handleBulkDelete = () => {
    if (selected.size === 0) return;
    setConfirmState({
      title: 'Delete selected QR codes?',
      body: (
        <>
          <Box sx={{ mb: 1 }}>{selected.size} QR code(s) will be deleted.</Box>
          <Box sx={{ color: '#718096', fontSize: 12 }}>Any codes still connected to a pet will be skipped.</Box>
          <Box sx={{ color: '#E53E3E', fontSize: 12, fontWeight: 600, mt: 1.5 }}>This cannot be undone.</Box>
        </>
      ),
      confirmLabel: `Delete ${selected.size}`,
      onConfirm: async () => {
        try {
          setBulkDeleting(true);
          const ids = Array.from(selected.keys());
          const res = await api.post('/admin/qrcodes/bulk-delete', { ids });
          const { deleted = 0, skipped = 0 } = res.data?.data || {};
          setActionResult({
            severity: skipped ? 'warning' : 'success',
            text: skipped
              ? `Deleted ${deleted}. Skipped ${skipped} connected code(s) — disconnect first.`
              : `Deleted ${deleted} QR code(s).`,
          });
          cancelSelectMode();
          fetchQRCodes();
          setConfirmState(null);
        } catch (e) {
          setActionResult({ severity: 'error', text: e.response?.data?.message || 'Failed to delete selected QR codes.' });
          setConfirmState(null);
        } finally {
          setBulkDeleting(false);
        }
      },
    });
  };

  const handleDeleteUnlinked = () => {
    const created = stats.created || 0;
    const disconnected = stats.disconnected || 0;
    const total = created + disconnected;
    if (total === 0) return;
    setConfirmState({
      title: 'Delete all unlinked QR codes?',
      body: (
        <>
          <Box sx={{ mb: 1.5 }}><strong>{total}</strong> unlinked QR code(s) will be deleted:</Box>
          <Box sx={{ pl: 1.5, fontSize: 13, lineHeight: 1.8 }}>
            <Box>&bull; {created} never connected</Box>
            <Box>&bull; {disconnected} previously disconnected</Box>
          </Box>
          <Box sx={{ color: '#718096', fontSize: 12, mt: 1.5 }}>
            Connected and deactivated codes will not be touched.
          </Box>
          <Box sx={{ color: '#E53E3E', fontSize: 12, fontWeight: 600, mt: 1 }}>This cannot be undone.</Box>
        </>
      ),
      confirmLabel: `Delete ${total}`,
      onConfirm: async () => {
        try {
          setDeletingUnlinked(true);
          const res = await api.post('/admin/qrcodes/delete-unlinked');
          const { deleted = 0 } = res.data?.data || {};
          setActionResult({ severity: 'success', text: `Deleted ${deleted} unlinked QR code(s).` });
          fetchQRCodes();
          setConfirmState(null);
        } catch (e) {
          setActionResult({ severity: 'error', text: e.response?.data?.message || 'Failed to delete unlinked QR codes.' });
          setConfirmState(null);
        } finally {
          setDeletingUnlinked(false);
        }
      },
    });
  };

  const pageSelectedCount = qrCodes.filter((qr) => selected.has(qr.id)).length;
  const allPageSelected = qrCodes.length > 0 && pageSelectedCount === qrCodes.length;
  const somePageSelected = pageSelectedCount > 0 && pageSelectedCount < qrCodes.length;

  const handleDownloadQR = (code) => {
    const svg = document.querySelector(`#qr-preview-${code.replace(/[^a-zA-Z0-9]/g, '')} svg`);
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 400;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, 400, 400);
      ctx.drawImage(img, 0, 0, 400, 400);
      const link = document.createElement('a');
      link.download = `${code}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const fetchQRCodes = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = { page: page + 1, limit: rowsPerPage };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const res = await api.get('/admin/qrcodes', { params });
      const data = res.data.data;
      setQrCodes(data.qrCodes || []);
      setTotal(data.total || 0);
      setStats(data.stats || {});
    } catch (e) {
      console.error('Error fetching QR codes:', e);
      setError('Failed to load QR codes.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, statusFilter]);

  useEffect(() => { fetchQRCodes(); }, [fetchQRCodes]);

  const handleSearchChange = useCallback((e) => {
    const val = e.target.value;
    setSearchInput(val);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => { setSearch(val); setPage(0); }, 300);
  }, []);

  useEffect(() => () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); }, []);

  const handleDelete = async (qr) => {
    if (!window.confirm(`Delete QR code ${qr.code}? This cannot be undone.`)) return;
    try {
      setDeleting(qr.id);
      await api.delete(`/admin/qrcodes/${qr.id}`);
      fetchQRCodes();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to delete');
    } finally {
      setDeleting(null);
    }
  };

  const handleDisconnect = async (qr) => {
    if (!window.confirm(`Disconnect QR code ${qr.code} from ${qr.pet?.name}?`)) return;
    try {
      await api.put(`/admin/qrcodes/${qr.id}/disconnect`, {});
      fetchQRCodes();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to disconnect');
    }
  };

  const statCards = [
    { label: 'Total', value: stats.total || 0, color: '#32A6D8' },
    { label: 'Connected', value: stats.connected || 0, color: '#38A169' },
    { label: 'Available', value: (stats.created || 0) + (stats.disconnected || 0), color: '#DD6B20' },
    { label: 'Deactivated', value: stats.deactivated || 0, color: '#E53E3E' },
  ];

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {actionResult && (
        <Alert severity={actionResult.severity} sx={{ mb: 2 }} onClose={() => setActionResult(null)}>
          {actionResult.text}
        </Alert>
      )}

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {statCards.map((s) => (
          <Box
            key={s.label}
            sx={{
              flex: '1 1 140px', minWidth: 140, p: 2, background: '#FFF',
              borderRadius: '12px', border: '1px solid #EDF2F7', borderLeft: `3px solid ${s.color}`,
            }}
          >
            <Box sx={{ fontFamily: ff, fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</Box>
            <Box sx={{ fontFamily: ff, fontSize: 11, fontWeight: 600, color: '#718096' }}>{s.label}</Box>
          </Box>
        ))}
      </Box>

      {/* Filters */}
      <FilterBar>
        <SearchInput value={searchInput} onChange={handleSearchChange} placeholder="Search by code, pet name, notes..." />
        <FilterSelect
          label="Status"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          options={[
            { value: '', label: 'All Statuses' },
            { value: 'CREATED', label: 'Created' },
            { value: 'CONNECTED', label: 'Connected' },
            { value: 'DISCONNECTED', label: 'Disconnected' },
            { value: 'DEACTIVATED', label: 'Deactivated' },
          ]}
        />
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          {!selectionMode ? (
            <>
              <Button
                variant="outlined"
                startIcon={<CheckSquare size={16} />}
                onClick={enterSelectMode}
                sx={{ fontFamily: ff, textTransform: 'none', borderColor: '#32A6D8', color: '#32A6D8', borderRadius: '10px', fontWeight: 600, '&:hover': { borderColor: '#2B8FB8', background: '#F0F9FF' } }}
              >
                Select
              </Button>
              {(() => {
                const unlinkedCount = (stats.created || 0) + (stats.disconnected || 0);
                return (
                  <Button
                    variant="outlined"
                    startIcon={deletingUnlinked ? <CircularProgress size={14} color="inherit" /> : <Trash2 size={16} />}
                    onClick={handleDeleteUnlinked}
                    disabled={unlinkedCount === 0 || deletingUnlinked}
                    sx={{ fontFamily: ff, textTransform: 'none', borderRadius: '10px', fontWeight: 600, color: '#E53E3E', borderColor: '#FEB2B2', '&:hover': { borderColor: '#E53E3E', background: '#FFF5F5' } }}
                  >
                    Delete Unlinked ({unlinkedCount})
                  </Button>
                );
              })()}
              <Button
                variant="contained"
                startIcon={<Plus size={16} />}
                onClick={() => setGenerateOpen(true)}
                sx={{ fontFamily: ff, textTransform: 'none', background: '#32A6D8', borderRadius: '10px', fontWeight: 600, '&:hover': { background: '#2B8FB8' } }}
              >
                Generate QR Codes
              </Button>
            </>
          ) : (
            <>
              <Box sx={{ fontFamily: ff, fontSize: 13, fontWeight: 600, color: '#32A6D8', display: 'flex', alignItems: 'center', px: 1 }}>
                {selected.size} selected
              </Box>
              <Button
                variant="outlined"
                onClick={cancelSelectMode}
                disabled={exporting || bulkDeleting}
                sx={{ fontFamily: ff, textTransform: 'none', borderRadius: '10px', color: '#718096', borderColor: '#CBD5E0' }}
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                startIcon={bulkDeleting ? <CircularProgress size={14} color="inherit" /> : <Trash2 size={16} />}
                onClick={handleBulkDelete}
                disabled={selected.size === 0 || exporting || bulkDeleting}
                sx={{ fontFamily: ff, textTransform: 'none', borderRadius: '10px', fontWeight: 600, color: '#E53E3E', borderColor: '#FEB2B2', '&:hover': { borderColor: '#E53E3E', background: '#FFF5F5' } }}
              >
                {bulkDeleting ? 'Deleting...' : `Delete Selected (${selected.size})`}
              </Button>
              <Button
                variant="contained"
                startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <Download size={16} />}
                onClick={handleExportSelected}
                disabled={selected.size === 0 || exporting || bulkDeleting}
                sx={{ fontFamily: ff, textTransform: 'none', background: '#38A169', borderRadius: '10px', fontWeight: 600, '&:hover': { background: '#2F855A' } }}
              >
                {exporting
                  ? `Generating ${exportProgress.current}/${exportProgress.total}...`
                  : `Export Selected (${selected.size})`}
              </Button>
            </>
          )}
        </Box>
      </FilterBar>

      {/* Table */}
      {loading ? (
        <TableSkeleton columns={7} rows={6} />
      ) : (
        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {selectionMode && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allPageSelected}
                      indeterminate={somePageSelected}
                      onChange={togglePage}
                      sx={{ color: '#CBD5E0', '&.Mui-checked': { color: '#32A6D8' }, '&.MuiCheckbox-indeterminate': { color: '#32A6D8' } }}
                    />
                  </TableCell>
                )}
                <TableCell>Code</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Linked Pet</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Events</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {qrCodes.map((qr) => {
                const sc = STATUS_CONFIG[qr.status] || STATUS_CONFIG.CREATED;
                const isSelected = selected.has(qr.id);
                return (
                  <TableRow key={qr.id} selected={selectionMode && isSelected} hover={selectionMode} onClick={selectionMode ? () => toggleRow(qr) : undefined} sx={selectionMode ? { cursor: 'pointer' } : undefined}>
                    {selectionMode && (
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onChange={() => toggleRow(qr)}
                          sx={{ color: '#CBD5E0', '&.Mui-checked': { color: '#32A6D8' } }}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <QrCode size={16} color="#32A6D8" />
                        <Box sx={{ fontFamily: 'monospace', fontSize: 12, color: '#32A6D8', fontWeight: 600 }}>{qr.code}</Box>
                        <Tooltip title="View QR">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setQrPreview({ anchorEl: e.currentTarget, code: qr.code }); }} sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}>
                            <Maximize2 size={12} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copy code">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(qr.code); }} sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}>
                            <ClipboardCopy size={12} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell><StatusChip label={sc.label} color={sc.color} /></TableCell>
                    <TableCell>
                      {qr.pet ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar src={qr.pet.photoUrl} sx={{ width: 28, height: 28, fontSize: 11 }}>{qr.pet.name?.[0]}</Avatar>
                          <Box>
                            <Box sx={{ fontFamily: ff, fontSize: 12, fontWeight: 500 }}>{qr.pet.name}</Box>
                            <Box sx={{ fontFamily: ff, fontSize: 10, color: '#A0AEC0' }}>{qr.pet.type}</Box>
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ fontFamily: ff, fontSize: 12, color: '#CBD5E0' }}>-</Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ fontFamily: ff, fontSize: 12 }}>{qr.createdBy?.fullName || 'Admin'}</Box>
                    </TableCell>
                    <TableCell>{formatDate(qr.createdAt)}</TableCell>
                    <TableCell>
                      <StatusChip label={`${qr._count?.history || 0} events`} color={qr._count?.history > 0 ? '#32A6D8' : '#A0AEC0'} />
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => setDetailId(qr.id)} sx={{ color: '#32A6D8' }}>
                            <Eye size={15} />
                          </IconButton>
                        </Tooltip>
                        {(qr.status === 'CREATED' || qr.status === 'DISCONNECTED') && (
                          <Tooltip title="Connect to Pet">
                            <IconButton size="small" onClick={() => setConnectQR(qr)} sx={{ color: '#38A169' }}>
                              <Link size={15} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {qr.status === 'CONNECTED' && (
                          <Tooltip title="Disconnect">
                            <IconButton size="small" onClick={() => handleDisconnect(qr)} sx={{ color: '#DD6B20' }}>
                              <Unlink size={15} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {qr.status !== 'CONNECTED' && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(qr)}
                              disabled={deleting === qr.id}
                              sx={{ color: '#E53E3E', '&:hover': { background: '#FFF5F5' } }}
                            >
                              <Trash2 size={15} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {qrCodes.length === 0 && <EmptyState colSpan={selectionMode ? 8 : 7} message="No QR codes found." />}
            </TableBody>
          </Table>
          <StyledPagination
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(e, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100, 200, 500, 1000]}
          />
        </StyledTableContainer>
      )}

      {/* QR Preview Popover */}
      <Popover
        open={!!qrPreview}
        anchorEl={qrPreview?.anchorEl}
        onClose={() => setQrPreview(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{ paper: { sx: { borderRadius: '16px', p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 } } }}
      >
        {qrPreview && (
          <>
            <Box id={`qr-preview-${qrPreview.code.replace(/[^a-zA-Z0-9]/g, '')}`} sx={{ p: 2, background: '#FFF', borderRadius: '12px', border: '1px solid #EDF2F7' }}>
              <QRCodeSVG value={buildQrUrl(qrPreview.code)} size={200} level="H" fgColor={QR_COLOR} />
            </Box>
            <Box sx={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 700, color: '#1A202C', letterSpacing: 1 }}>
              {qrPreview.code}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                startIcon={<Download size={14} />}
                onClick={() => handleDownloadQR(qrPreview.code)}
                variant="contained"
                sx={{ fontFamily: ff, textTransform: 'none', fontSize: 12, background: '#32A6D8', borderRadius: '8px', '&:hover': { background: '#2B8FB8' } }}
              >
                Download PNG
              </Button>
              <Button
                size="small"
                startIcon={<ClipboardCopy size={14} />}
                onClick={() => { navigator.clipboard.writeText(qrPreview.code); }}
                variant="outlined"
                sx={{ fontFamily: ff, textTransform: 'none', fontSize: 12, borderRadius: '8px' }}
              >
                Copy Code
              </Button>
            </Box>
          </>
        )}
      </Popover>

      {/* Dialogs */}
      <GenerateDialog open={generateOpen} onClose={() => setGenerateOpen(false)} onGenerated={() => fetchQRCodes()} />
      <ConnectDialog open={!!connectQR} qrCode={connectQR} onClose={() => setConnectQR(null)} onConnected={() => fetchQRCodes()} />
      <DetailDialog open={!!detailId} qrId={detailId} onClose={() => setDetailId(null)} onUpdated={() => fetchQRCodes()} />
      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.title}
        body={confirmState?.body}
        confirmLabel={confirmState?.confirmLabel}
        busy={bulkDeleting || deletingUnlinked}
        onCancel={() => setConfirmState(null)}
        onConfirm={() => confirmState?.onConfirm?.()}
      />
    </Box>
  );
}
